export const meta = {
  id: '34-excuses',
  order: 34,
  checkFocus: { tense: ['imperfect'] },   // what the end-of-lesson check tests first
  titleFi: 'Tekosyyt',
  titleEn: 'Excuses',
  duolingoUnit: 'duo-40-excuses',
  // + Sanapaja's own words this lesson first needs (docs/COURSE.md "Own words in the lessons"; waves/wave-NN/lessons.json)
  lemmaIds: ['vāedagon', 'gīmīmagon', 'ours:kulēlaksir', 'ours:usōvenon', 'ours:jollōrnon', 'ours:ondurilloqitta', 'ours:albilennon', 'ours:aerāzma', 'ours:nāmanda', 'ours:mīkelilion', 'ours:qopsir', 'ours:korinon', 'ours:sahion', 'ours:ēdrunāzma', 'ours:dāerūljagon', 'ours:sētōñe', 'ours:nehunon', 'ours:lōtūlnon', 'ours:pelrāzma', 'ours:grēgio', 'ours:popkorni', 'ours:benzini', 'ours:hiniknon', 'ours:kolari', 'ours:oznehurion', 'ours:upsi', 'ours:zālilaksir'],
  exampleIds: [
    'got-s5e04-020',
    'ex-88919989f2ea',
    'ex-11675da963cd',
    'ex-22f79993abdf',
    'got-s5e04-003',
    'hotd-s3e08-002',
    'hotd-s1e05-002',
    'ex-25b28a1d4c90',
    'ex-4787c47959a5',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar',
    'https://wiki.languageinvention.com/index.php?title=Gīmīmagon',
    'https://wiki.languageinvention.com/index.php?title=Vāedagon',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammar: ['imperfect'],
  drills: { tense: ['imperfect'] },
  showLines: ['got-s5e04-020', 'got-s5e04-003', 'hotd-s3e08-002', 'hotd-s1e05-002'],
  pronunciation: { focus: 'r', words: ['qaedar', 'egry', 'jorrāeliarza', 'jaelarys', 'vaoresagon'], lines: ['got-s5e04-020', 'got-s5e04-003'] },
  letter: {
    fi: 'Kirjoita tekosyy: mitä olit tekemässä, kun et ehtinyt opiskella (imperfekti).',
    en: 'Write an excuse: what you were doing when you had no time to study (imperfect).',
  },
  scene: 'got-s5e04-1',
}

# Tekosyyt

– Miksi et tullut neuvoston kokoukseen?

– Koska olin syöttämässä vuohta lohikäärmeelleni, tietysti!

Hyvä tekosyy ei kerro, mitä teit, vaan mitä olit *tekemässä* sillä hetkellä, kun sinua odotettiin muualla. Korkeavalyriassa on tähän oma aikamuotonsa, **imperfekti**. Tällä oppitunnilla opit sen indikatiivin ja subjunktiivin sekä lopuksi verbin, joka jakaa sijat toisin kuin suomalainen odottaisi.

## Imperfekti: hetki, joka oli vielä kesken

Imperfekti kuvaa tekemistä, joka oli menneisyydessä käynnissä. Tips & Notes antaa parin, jossa ero näkyy puhtaimmillaan:

- <Hv glossed>Ñuhi zaldrīzī kisikilen.</Hv> – ’Olin syöttämässä lohikäärmettäni.’
- <Hv glossed>Ñuhi zaldrīzī kisittan.</Hv> – ’Syötin lohikäärmeeni.’

Jälkimmäinen on perfekti, jonka opit <LessonLink n={22}>oppitunnilla 22</LessonLink>: teko on tehty ja ohi. Imperfektillä siirryt takaisin siihen menneeseen hetkeen, jolloin teko oli vielä kesken, ja katsot sitä sisältäpäin. Siksi se sopii tekosyihin ja taustan kuvaamiseen: *olin juuri…*, *kun hän oli…*.

<Note>Varo nimeä. Suomen koulukieliopissa *imperfekti* tarkoittaa tavallista mennyttä aikaa (*söin*, *luin*). Valyrian imperfekti on suppeampi: se vastaa suomen rakennetta *olin syömässä*, *olin lukemassa*. Suomen mennyt aika (koulukieliopin imperfekti) vastaa useimmiten valyrian perfektiä.</Note>

Suomikin tuntee tämän eron, mutta ilmaisee sen usein objektin sijalla eikä verbillä: *luin kirjaa* (kesken) ja *luin kirjan* (loppuun). Valyriassa objekti on molemmissa lauseissa akkusatiivissa; ero näkyy verbin päätteessä.

<Compare fi="luin kirja|a" hv="kisik|ilen" label="kesken ollut teko: partitiiviobjekti ~ imperfekti" />

<Compare fi="luin kirja|n" hv="kisitt|an" label="loppuun viety teko: kokonaisobjekti ~ perfekti" />

Molemmat aikamuodot voivat esiintyä samassa lauseessa. Daenerys kertoo, mikä oli käynnissä ja mikä sen katkaisi:

<Line id="got-s5e04-020" />

<Interlinear hv="Mīrīno pāstys iderennī jumb|iles se bisa vala ziry ossēntas." gloss="Meereen-GEN kansalainen-NOM oikeudenkäynti-ACC odottaa-IPFV.3SG ja tämä-LUN.NOM mies-NOM hän-ACC murhata-PRF.3SG" fi="Meereenin kansalainen odotti oikeudenkäyntiä, ja tämä mies murhasi hänet." />

Odottaminen (<Hv>jumbiles</Hv>) on käynnissä ollut tausta; murha (<Hv>ossēntas</Hv>) on yksittäinen teko, joka tapahtui sen keskellä.

## Imperfektin taivutus

Imperfekti on futuurin sukulainen, eikä yhdennäköisyys ole sattumaa. Muistat <LessonLink n={26}>oppitunnilta 26</LessonLink>, että futuurin vartalossa on *l*: konsonanttiin päättyvä juuri saa *-il-*, ja sen perään tulevat a-sarjan persoonapäätteet (<Hv glossed>vāedilza</Hv> ’hän laulaa’ tulevaisuudessa). Imperfektissä vartalo on täsmälleen sama, mutta sen perään tulee uusi päätesarja, **e-sarja**:

<Paradigm lemma="vāedagon" numbers="sg,pl" filter="indicative.active.imperfect" />

Päätteet ovat siis *-en, -ē, -es, -in, -ēt, -is*. Kaksi asiaa kannattaa huomata:

- **Monikon 1. persoonassa on n.** Preesensissä ’laulamme’ on <Hv>vāedi</Hv> ja futuurissa <Hv>vāedili</Hv>, mutta imperfektissä <Hv>vāedilin</Hv>. Tässä aikamuodossa *n* kuuluu 1. persoonaan kummassakin luvussa.
- **Pitkät vokaalit erottavat persoonia.** <Hv glossed>vāedilē</Hv> ’sinä olit laulamassa’, <Hv glossed>vāediles</Hv> ’hän oli laulamassa’, <Hv glossed>vāedilēt</Hv> ’te olitte laulamassa’. Suomalaisen korva kuulee pituuden luonnostaan, ja siitä on tässä hyötyä: pituus kantaa merkitystä.

Huomaa myös, että yksikön 1. persoona on säännöllinen <Hv>vāedilen</Hv>. Futuurin <Hv>vāedinna</Hv> syntyi äänteenmuutoksesta, jota imperfektissä ei ole.

<Pronounce word="kisikilen" />
<Pronounce word="vāedilēt" />

### Vokaaliin päättyvät juuret

Persoonapäätteet ovat samat, mutta *l*:n edellä oleva vokaali on pitkä, ja sen laatu riippuu juuren viimeisestä vokaalista. Sääntö on sama kuin futuurissa:

- juuren *a* tai *o* → *-ēl-*
- juuren *e*, *i* tai *u* → *-īl-*

Tips & Notes näyttää kaikki viisi tapausta monikon 3. persoonassa:

- <Hv glossed>ȳdrēlis</Hv> ’he olivat puhumassa’ (<Hv>ȳdragon</Hv>: a → ē)
- <Hv glossed>nektēlis</Hv> ’he olivat leikkaamassa’ (<Hv>nektogon</Hv>: o → ē)
- <Hv glossed>urnīlis</Hv> ’he olivat katsomassa’ (<Hv>urnegon</Hv>: e → ī)
- <Hv glossed>sindīlis</Hv> ’he olivat ostamassa’ (<Hv>sindigon</Hv>: i → ī)
- <Hv glossed>bardīlis</Hv> ’he olivat kirjoittamassa’ (<Hv>bardugon</Hv>: u → ī)

Jos osaat futuurin vartalon, osaat siis jo imperfektin vartalon. Vertaa: <Hv glossed>ȳdrēlzi</Hv> ’he puhuvat (tulevaisuudessa)’ ja <Hv glossed>ȳdrēlis</Hv> ’he olivat puhumassa’. Vain päätesarja vaihtuu.

### Imperfekti lauseissa

Tässä muutama esimerkki. Ensimmäinen on aito tekosyy:

<Example id="ex-88919989f2ea" />

<Example id="ex-11675da963cd" />

Imperfekti on usein tausta, jonka keskellä jotakin tapahtui. Tässä taustan katkaisee perfektin passiivi (<LessonLink n={30}>oppitunti 30</LessonLink>):

<Example id="ex-22f79993abdf" />

Myös olla-verbillä <Hv>sagon</Hv> on imperfekti. Sen vartalo on *īl-*, kuten <LessonLink n={3}>oppitunnilla 3</LessonLink> näit: <Hv fi="olin, olit, oli">īlen, īlē, īles</Hv> ja niin edelleen. Daenerys käyttää sitä kertoessaan, mikä vanki *oli*:

<Line id="got-s5e04-003" />

Imperfekti voi kuvata myös tilaa, joka jatkui koko menneisyyden ajan. Daemon sanoo Rhaenyralle <Hv>gīmīlē</Hv> (<Hv glossed>gīmigon</Hv> ’tietää’), ja englanninkielinen käännös vastaa suomen ’olet aina tiennyt’:

<Line id="hotd-s3e08-002" />

## Imperfektin subjunktiivi

Futuurissa on kaksi päätesarjaa: indikatiivissa a-sarja, subjunktiivissa u-sarja (<Hv>vāedilun</Hv>). Imperfektissä on sama kahtiajako, mutta vokaalit ovat toiset:

- futuuri: indikatiivi **a**-sarja, subjunktiivi **u**-sarja
- imperfekti: indikatiivi **e**-sarja, subjunktiivi **o**-sarja

O-sarjan päätteet tunnet jo: ne ovat samat *-on, -ō, -os, -oty, -ōt, -osy*, jotka opit preesensin subjunktiivista <LessonLink n={17}>oppitunnilla 17</LessonLink>. Ne liitetään imperfektin vartaloon, joten vokaalivartaloissa pätee sama *ē*/*ī*-sääntö:

<Paradigm lemma="nektogon" numbers="sg,pl" filter="subjunctive.active.imperfect" />

Konsonanttivartalossa (<Hv glossed>vāedagon</Hv> ’laulaa’) sama näyttää tältä: <Hv glossed>vāedilon, vāedilō, vāedilos, vāediloty, vāedilōt, vāedilosy</Hv>.

<Pronounce word="nektēlosy" />

Missä subjunktiivia sitten tarvitaan? Tähän mennessä tärkein käyttö on kielto: <LessonLink n={17}>oppitunnilla 17</LessonLink> opit, että kun lause kielletään sanalla <Hv>daor</Hv>, verbi vaihtuu indikatiivista subjunktiiviin. Sama pätee imperfektiin. Rhaenyra sanoo isälleen <Hv glossed>gīmīlon daor</Hv> ’en tiennyt’:

<Line id="hotd-s1e05-002" />

Tässä suomi ei auta. Suomessa kielto ilmaistaan kieltoverbillä (*en ollut*, *et tiennyt*), eikä pääverbin tapaluokka muutu. Valyriassa kieltosana tulee lauseen loppuun ja verbi siirtyy subjunktiiviin, myös menneessä ajassa.

## Opettaminen: gīmīmagon

<Hv glossed>Gīmīmagon</Hv> tarkoittaa ’opettaa, neuvoa, tiedottaa’. Sanakirja kertoo sen rakenteen: <Hv glossed>gīmigon</Hv> ’tietää’ + johdin *-emagon*, jolla muodostetaan verbejä merkityksessä ’saada joku joksikin, tehdä jostakin jotakin’. Opettaminen on siis kirjaimellisesti *saattaa joku tietämään*.

Tästä seuraa sijojen jako, ja siinä on ansa. Tips & Notesin mukaan

- **opetettava ihminen on akkusatiivissa** (suora objekti),
- **opetettava asia on datiivissa** (epäsuora objekti).

Suomessa sanotaan joko *opetan oppilaita* (partitiivi) tai *opetan oppilaille valyriaa*. Jälkimmäisessä allatiivin *-lle* tulee ihmiselle, ja juuri siksi korva tarjoaa datiivia väärään paikkaan. Valyriassa datiivin saa opetettava aihe, ei ihminen.

<Compare fi="oppilai|ta" hv="jollōrir|ossa" label="opetettava ihminen: partitiivi ~ akkusatiivi" />

<Example id="ex-25b28a1d4c90" />

<Interlinear hv="Zān ñuhī jollōrir|ossa gīmīm|ilen." gloss="eilen minun-SOL.ACC.PL oppilas-ACC.PL opettaa-IPFV.1SG" fi="Olin eilen opettamassa oppilaitani." />

Perfektissä verbillä on oma vartalonsa: <Hv glossed>gīmēdan</Hv> ’opetin’. Imperfektissä vartalo on säännöllinen: <Hv>gīmīm-</Hv> + <Hv>-ilen</Hv>.

<Pronounce word="gīmīmagon" />

Toinen uusi verbi, <Hv glossed>vāedagon</Hv> ’laulaa’, on konsonanttivartaloinen, ja sen imperfektin näit jo yllä. Tässä se on preesensissä:

<Example id="ex-4787c47959a5" />

## Harjoituslauseita

<Practice hv="Taoba vāediles." fi="Poika oli laulamassa." />

<Practice hv="Zaldrīzes ēdrīles." fi="Lohikäärme oli nukkumassa." />

<Practice hv="Ābra vāedilos daor." fi="Nainen ei ollut laulamassa." />

<Practice hv="Taobe udrirti gīmīmilen." fi="Olin opettamassa pojalle kieltä." />

Viimeisessä opetettava poika on akkusatiivissa (<Hv>taobe</Hv>) ja opetettava kieli datiivissa (<Hv l="udrir">udrirti</Hv>, kollektiivisanan <Hv l="udrir">udrir</Hv> datiivi).

## Sanat

<Vocab />

<Check />
